import { createFileRoute, Link } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { Drama, Globe, Import, Loader2, Lock, Pencil, Plus, ShieldOff } from "lucide-react";
import { AdminAccessNotice } from "@/components/admin/access-notice";
import { PersonaImportDialog } from "@/components/admin/persona-import-dialog";
import { AppLayout } from "@/components/app-layout";
import { PersonaDialog, type PersonaEditable } from "@/components/chat/persona-dialog";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { aws as awsClient } from "@/aws/client";
import { useAuth } from "@/hooks/use-auth";
import { useIsAdmin } from "@/hooks/use-is-admin";
import { readAllRows } from "@/lib/read-all-rows";

export const Route = createFileRoute("/admin/personas")({ component: AdminPersonasPage });

interface Persona extends PersonaEditable {
  user_id: string;
  updated_at: string;
}

function AdminPersonasPage() {
  const { user } = useAuth();
  const { isAdmin, loading: roleLoading, error: accessError, retry: retryAccess } = useIsAdmin();
  const [personas, setPersonas] = useState<Persona[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [importing, setImporting] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState<Persona | null>(null);

  const refresh = async () => {
    if (!user) return;
    setLoading(true);
    setLoadError(null);
    try {
      const rows = await readAllRows<Persona>((from, to) =>
        awsClient
          .from("personas")
          .select("id,user_id,name,description,system_prompt,is_shared,updated_at")
          .eq("user_id", user.id)
          .order("name", { ascending: true })
          .order("id", { ascending: true })
          .range(from, to),
      );
      if (rows.error) throw new Error(rows.error.message);
      setPersonas(rows.data);
    } catch (error) {
      setLoadError(error instanceof Error ? error.message : "Could not load personas.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (isAdmin && user) void refresh();
  }, [isAdmin, user?.id]);

  if (roleLoading) {
    return (
      <AppLayout>
        <div className="flex h-full items-center justify-center">
          <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
        </div>
      </AppLayout>
    );
  }
  if (accessError) {
    return (
      <AppLayout>
        <div className="p-6">
          <AdminAccessNotice error={accessError} retry={retryAccess} />
        </div>
      </AppLayout>
    );
  }
  if (!isAdmin) {
    return (
      <AppLayout>
        <div className="flex h-full items-center justify-center px-6">
          <Card className="max-w-md">
            <CardContent className="p-8 text-center">
              <ShieldOff className="mx-auto h-10 w-10 text-muted-foreground" />
              <h1 className="mt-4 text-ui-xl font-bold text-foreground">Admins only</h1>
              <p className="mt-2 text-ui-base text-muted-foreground">You need the admin role to import personas.</p>
              <Button asChild variant="outline" className="mt-6">
                <Link to="/chat">Back to chat</Link>
              </Button>
            </CardContent>
          </Card>
        </div>
      </AppLayout>
    );
  }

  return (
    <AppLayout>
      <div className="flex-1 overflow-y-auto">
        <div className="mx-auto max-w-4xl px-6 py-8">
          <div className="mb-6 flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
            <div>
              <h1 className="text-ui-2xl font-bold text-foreground">Personas</h1>
              <p className="text-ui-base text-muted-foreground">
                Import and manage reusable characters for AskBell conversations.
              </p>
            </div>
            <div className="flex flex-wrap gap-2">
              <Button variant="outline" onClick={() => setImporting(true)}>
                <Import className="h-4 w-4" />
                Import
              </Button>
              <Button
                onClick={() => {
                  setEditing(null);
                  setDialogOpen(true);
                }}
              >
                <Plus className="h-4 w-4" />
                New persona
              </Button>
            </div>
          </div>

          {loading ? (
            <div className="flex justify-center py-12">
              <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
            </div>
          ) : loadError ? (
            <Card>
              <CardContent role="alert" className="space-y-3 p-8 text-center">
                <p className="text-ui-base text-destructive">{loadError}</p>
                <Button variant="outline" onClick={refresh}>
                  Retry loading personas
                </Button>
              </CardContent>
            </Card>
          ) : personas.length === 0 ? (
            <Card>
              <CardContent className="p-10 text-center">
                <Drama className="mx-auto h-8 w-8 text-muted-foreground" />
                <p className="mt-3 text-ui-base font-bold text-foreground">No personas yet</p>
                <p className="mt-1 text-ui-sm text-muted-foreground">
                  Import Markdown files or create a persona from scratch.
                </p>
                <Button className="mt-4" variant="outline" onClick={() => setImporting(true)}>
                  <Import className="h-4 w-4" />
                  Import personas
                </Button>
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-2">
              {personas.map((persona) => (
                <Card key={persona.id}>
                  <CardContent className="flex items-start gap-3 p-4">
                    <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-ui-md bg-primary/10 text-primary">
                      <Drama className="h-4 w-4" />
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="flex flex-wrap items-center gap-2">
                        <p className="text-ui-base font-bold text-foreground">{persona.name}</p>
                        <span
                          className={`inline-flex items-center gap-1 rounded px-1.5 py-0.5 text-ui-xs font-bold ${persona.is_shared ? "bg-primary/10 text-primary" : "bg-muted text-muted-foreground"}`}
                        >
                          {persona.is_shared ? <Globe className="h-2.5 w-2.5" /> : <Lock className="h-2.5 w-2.5" />}
                          {persona.is_shared ? "Shared" : "Private"}
                        </span>
                      </div>
                      {persona.description && (
                        <p className="mt-0.5 line-clamp-2 text-ui-sm text-muted-foreground">
                          {persona.description}
                        </p>
                      )}
                      <p className="mt-1 text-ui-xs text-muted-foreground">
                        {persona.system_prompt.length.toLocaleString()} characters
                      </p>
                    </div>
                    <Button
                      size="icon"
                      variant="ghost"
                      aria-label={`Edit ${persona.name}`}
                      onClick={() => {
                        setEditing(persona);
                        setDialogOpen(true);
                      }}
                    >
                      <Pencil className="h-4 w-4" />
                    </Button>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </div>
      </div>

      <PersonaImportDialog open={importing} onClose={() => setImporting(false)} onImported={refresh} />
      <PersonaDialog open={dialogOpen} onOpenChange={setDialogOpen} persona={editing} onSaved={refresh} />
    </AppLayout>
  );
}
